(() => {
  'use strict';

  const FORM_SELECTOR = 'product-form form[data-type="add-to-cart-form"]';
  const DRAWER_SELECTOR = '#minicart_wrapper';
  const DRAWER_FORM_SELECTOR = '#minicart-form';
  const PENDING_SELECTOR = '[data-broek-cart-pending]';
  let requestPending = false;

  function parseCount(value) {
    const match = String(value || '').match(/\d+/);
    return match ? Number(match[0]) : 0;
  }

  function updateCartCount(itemCount) {
    document.querySelectorAll('.cart-count').forEach((element) => {
      if (element.classList.contains('cart-count-drawer')) {
        element.textContent = `(${itemCount})`;
      } else {
        element.textContent = itemCount > 100 ? '~' : String(itemCount);
      }
    });
  }

  function isStandardProductForm(form) {
    if (!form.matches('[data-type="add-to-cart-form"]')) return false;
    if (!form.closest('product-form')) return false;
    if (form.closest('#product-quickview, .tingle-modal, product-form-quick-edit')) return false;
    if (form.closest('product-bundle, product-group, [data-bundle]')) return false;
    return !Array.from(form.elements).some((field) =>
      String(field.name || '').startsWith('items[')
    );
  }

  function setButtonState(button, loading, preserveDisabled = false) {
    if (!button) return;
    if (!preserveDisabled) button.disabled = loading;
    button.classList.toggle('loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function errorHost(form) {
    const productForm = form.closest('product-form');
    return form.closest('.sticky__item-info')
      ? document.querySelector('.button-trigger__sticky product-form') || productForm
      : productForm;
  }

  function showFormError(form, message) {
    const productForm = errorHost(form);
    if (!productForm) return;
    let wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      form.prepend(wrapper);
    }
    let output = wrapper.querySelector('.product-form__error-message');
    if (!output) {
      output = document.createElement('span');
      output.className = 'product-form__error-message';
      wrapper.append(output);
    }
    output.textContent = /cannot find variant/i.test(message || '')
      ? 'This item is currently unavailable. Please try another option.'
      : message || 'Unable to add this item. Please try again.';
    wrapper.setAttribute('role', 'alert');
    wrapper.setAttribute('tabindex', '-1');
    wrapper.hidden = false;
    wrapper.focus({ preventScroll: true });
    wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    drawer.classList.add('open');
    document.querySelector('.overlayminicart')?.classList.add('open');
  }

  function clearPendingStatus() {
    document.querySelector(PENDING_SELECTOR)?.remove();
  }

  function replaceElement(current, next) {
    if (!current || !next) return;
    current.replaceWith(next);
  }

  function refreshDrawerMarkup(html, drawer) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const currentForm = document.querySelector(DRAWER_FORM_SELECTOR);
    const nextForm = parsed.querySelector(DRAWER_FORM_SELECTOR);
    if (!currentForm || !nextForm) throw new Error('Cart drawer section is missing');
    currentForm.innerHTML = nextForm.innerHTML;

    replaceElement(
      document.querySelector('#minicart_wrapper free-ship-progress-bar.cart_shipping'),
      parsed.querySelector('free-ship-progress-bar.cart_shipping')
    );
    replaceElement(
      document.querySelector('#minicart_wrapper .cart-countdown-time'),
      parsed.querySelector('.cart-countdown-time')
    );

    if (typeof drawer?.cartAction === 'function') drawer.cartAction();
    if (window.BlsLazyloadImg?.init) window.BlsLazyloadImg.init();
    clearPendingStatus();
    window.dispatchEvent(new CustomEvent('broek:cart-drawer-refreshed'));
  }

  async function fetchDrawerSection() {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('section_id', 'minicart-form');
    url.searchParams.set('_', Date.now().toString());

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) throw new Error('Cart drawer refresh failed');
    return response.text();
  }

  async function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
    if (!isStandardProductForm(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (requestPending || form.dataset.broekFastCartPending === 'true') return;

    const submitButton = event.submitter || form.querySelector('[type="submit"][name="add"]');
    if (submitButton?.disabled) return;
    const submitLabel = submitButton?.textContent;
    const drawer = document.querySelector(DRAWER_SELECTOR);
    const countElement = document.querySelector('.cart-count-drawer');
    const currentCount = parseCount(countElement?.textContent);
    const formData = new FormData(form);
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const nextCount = currentCount + quantity;
    let addSucceeded = false;

    formData.set('sections', 'minicart-form');
    formData.set('sections_url', window.location.pathname);
    form.dataset.broekFastCartPending = 'true';
    requestPending = true;
    document.dispatchEvent(new CustomEvent('broek:cart-mutation-start'));
    setButtonState(submitButton, true);

    const previousError = errorHost(form)?.querySelector('.product-form__error-message-wrapper');
    if (previousError) previousError.hidden = true;
    const drawerRevision = drawer?.drawerRevision;

    try {
      const addUrl = `${window.routes?.cart_add_url || '/cart/add'}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item.');
      }

      addSucceeded = true;
      updateCartCount(nextCount);
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { source: 'broek-fast-cart', item: result },
      }));

      let drawerHtml = result.sections?.['minicart-form'];
      if (!drawerHtml || !new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector(DRAWER_FORM_SELECTOR)) {
        try {
          drawerHtml = await fetchDrawerSection();
        } catch (firstRefreshError) {
          await new Promise((resolve) => setTimeout(resolve, 350));
          drawerHtml = await fetchDrawerSection();
        }
      }
      refreshDrawerMarkup(drawerHtml, drawer);
      if (drawer?.drawerRevision === drawerRevision) openDrawer(drawer);
    } catch (error) {
      clearPendingStatus();
      if (!addSucceeded) {
        updateCartCount(currentCount);
        showFormError(form, error.message);
      } else {
        window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
      }
    } finally {
      delete form.dataset.broekFastCartPending;
      requestPending = false;
      setButtonState(submitButton, false, submitButton?.textContent !== submitLabel);
      document.dispatchEvent(new CustomEvent('broek:cart-mutation-end'));
    }
  }

  document.addEventListener('submit', handleSubmit, true);
})();
